'use client';

import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { OrderContextProvider } from './state/order/OrderContext';
import { GlobalStyle } from './globalStyles';
import { useAddToHomeScreen } from './utils/pwa/useAddToHomeScreen';
import { ErrorContextProvider } from './components/error/ErrorContext';
import { ErrorBoundary } from './components/error/ErrorBoundary';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { isDevMode } from './utils/isDevMode';
import { TagManager, type TagManagerArgs } from './utils/tagmanager';
import { initAppInsights, trackInsightsException } from './utils/telemetry/appinsights';
import VersionCheck from './components/versionCheck/VersionCheck';
import { UserContextProvider } from './state/user/UserContext';
import styled, { IStyleSheetContext, StyleSheetManager } from 'styled-components';
import { getIsNativePlatform, useCapacitor } from './useCapacitor';
import AppNativeHandlers from './AppNativeHandlers';
import { CmsRefreshOnResume } from './components/storyblok/CmsRefreshOnResume';
import { getString } from './utils/config';
import { HOUR_IN_MS } from './utils/utils';
import isPropValid from '@emotion/is-prop-valid';
import { Capacitor } from '@capacitor/core';
import { Header, hiddenHeaderPaths } from './components/header';
import { AppHeader } from './components/header/AppHeader';
import { Footer, hiddenFooterPaths } from './components/footer/Footer';
import { LoadingSuspense } from './routes/components/LoadingSuspense';
import { AppSafeAreaContainer } from './components/appSafeAreaContainer/AppSafeAreaContainer';
import EnforceLoyalty from './components/loyalty/enforce-modal/EnforceLoyalty';
import { AppAnalyticsConsentSync } from './components/analytics/AppAnalyticsConsentSync';
import { BottomNavigation } from './components/bottomNavigation/BottomNavigation';
import { AppWrapper } from './AppWrapper';
import { usePathname, useRouter } from 'next/navigation';
import { AppForcedUpdatePage } from './components/appForcedUpdatePage/AppForcedUpdatePage';
import { AppLoadingScreen } from './components/appLoadingScreen/AppLoadingScreen';
import { initAppPushNotifications } from './appPushNotifications';
import { useTrackNavigationHistory } from './hooks/navigationHooks';

const registerServiceWorker = async (): Promise<void> => {
  if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
    try {
      const registration = await navigator.serviceWorker.register('/serviceWorker.js');

      // Force update service worker once per hour
      // (some people might leave the PWA open for a long time, and as the page is an SPA, page reloads might not happen)
      setInterval(() => {
        registration.update();
      }, HOUR_IN_MS);
    } catch (e) {
      trackInsightsException('FRONTEND_SERVICE_WORKER_ERROR', e);
    }
  }
};

const tagManagerArgs: TagManagerArgs = {
  gtmId: 'GTM-K4TN28P',
  auth: getString(process.env.NEXT_PUBLIC_GTM_AUTH),
  preview: getString(process.env.NEXT_PUBLIC_GTM_PREVIEW),
};

initAppInsights();

// Service worker not needed in iOS/Android app
// TODO: TagManager should be replaced with FirebaseAnalytics implementation in the app
if (!Capacitor.isNativePlatform()) {
  registerServiceWorker();
  TagManager.initialize(tagManagerArgs);
}

type Props = {
  children: React.ReactNode;
};

const App: React.FC<Props> = ({ children }) => {
  useAddToHomeScreen();
  useTrackNavigationHistory();
  const { isAppReady, hasToDoForcedUpdate, isDownloading, downloadProgress } = useCapacitor();
  const pathname = usePathname();
  const router = useRouter();

  // Lazily create the QueryClient once per App mount. Creating it in the render
  // body would build a fresh, empty cache on every navigation (App re-renders on
  // pathname changes), discarding already-fetched data and forcing refetches.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60000, // Default is 0, which means everything is refetched on every mount.
          },
        },
      })
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Add env for E2E tests
      window.env = getString(process.env.NEXT_PUBLIC_APP_ENV);
    }

    // Initialize push notification listeners, won't initialize again if already done
    if (isAppReady) {
      initAppPushNotifications(router);
    }
  }, [isAppReady, router]);

  // This implements the default behavior from styled-components v5
  const shouldForwardProp: IStyleSheetContext['shouldForwardProp'] = (propName, target) => {
    if (typeof target === 'string') {
      // For HTML elements, forward the prop if it is a valid HTML attribute
      return isPropValid(propName);
    }
    // For other elements, forward all props
    return true;
  };

  const headerHidden = hiddenHeaderPaths.some((path) => pathname.includes(path));
  const footerHidden = hiddenFooterPaths.some((path) => pathname.includes(path));

  if (hasToDoForcedUpdate) {
    return (
      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <LoadingSuspense>
          <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
              <GlobalStyle />
              <AppForcedUpdatePage />
            </QueryClientProvider>
          </ErrorBoundary>
        </LoadingSuspense>
      </StyleSheetManager>
    );
  }

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <LoadingSuspense>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            {isDownloading && <AppLoadingScreen progress={downloadProgress} />}
            <ErrorContextProvider>
              <UserContextProvider>
                <OrderContextProvider>
                  <GlobalStyle />
                  <AppNativeHandlers />
                  <CmsRefreshOnResume />
                  <AppWrapper>
                    <AppSafeAreaContainer>
                      {!headerHidden && (Capacitor.isNativePlatform() ? <AppHeader /> : <Header />)}
                      <StyledContainer>{children}</StyledContainer>
                      <EnforceLoyalty />
                      {getIsNativePlatform() && <AppAnalyticsConsentSync />}
                      {getIsNativePlatform() ? <BottomNavigation /> : !footerHidden && <Footer />}
                    </AppSafeAreaContainer>
                  </AppWrapper>
                  {!getIsNativePlatform() && <VersionCheck />}
                </OrderContextProvider>
              </UserContextProvider>
            </ErrorContextProvider>
            {isDevMode() && <ReactQueryDevtools initialIsOpen={false} />}
          </QueryClientProvider>
        </ErrorBoundary>
      </LoadingSuspense>
    </StyleSheetManager>
  );
};

export default App;

const StyledContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;
